import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  ClickAwayListener,
} from '@mui/material';
import {
  DashboardOutlined,
  PeopleOutlined,
  BusinessOutlined,
  DescriptionOutlined,
  SendOutlined,
  ArticleOutlined,
  FolderOutlined,
  InsertDriveFileOutlined,
  GroupOutlined,
  WarehouseOutlined,
  Menu as MenuIcon,
  SecurityOutlined,
  VpnKeyOutlined,
  Logout,
  ContactSupportOutlined,
  AssignmentOutlined,
  StorageOutlined,
  KeyboardArrowDown,
  Search as SearchIcon,
  KeyboardReturnOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material';
import { useState, useRef, useEffect } from 'react';
import { Select, FormControl } from '@mui/material';
import axiosInstance from '../api/axiosConfig';
import { useAuth } from '../hooks/useAuth';
import { companyService } from '../modules/companies';
import userService from '../modules/users/services/userService';
import { NotificationPanel } from '../modules/notifications';
import ImpersonationBanner from '../components/ImpersonationBanner';
import { getAvatarConfig, isAvatarUrl } from '../utils/avatarUtils';

const drawerWidth = 256;

// Industry sidebar tokens (light sidebar, dark active)
const SIDEBAR = {
  bg: '#f2f2f3',
  hover: 'rgba(29,31,32,0.06)',
  active: '#1d2d3d',
  activeBorder: '#5980a6',
  text: '#424244',
  textActive: '#f2f2f3',
  textHover: '#1d1f20',
  divider: 'rgba(29,31,32,0.16)',
  icon: '#98989b',
  iconActive: '#f2f2f3',
  groupLabel: 'rgba(29,31,32,0.4)',
};

const MainLayout = () => {
  const { user, logout, updateUser, isOwner, isModuleEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [topbarCompanies, setTopbarCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState('');

  useEffect(() => {
    if (isOwner) {
      companyService.getAll({ limit: 100 }).then(res => {
        setTopbarCompanies(res.data || []);
      }).catch(() => {});
    }
  }, [isOwner]);

  useEffect(() => {
    if (user?.companyId) setActiveCompanyId(user.companyId.toString());
    else if (user?.company?.id) setActiveCompanyId(user.company.id.toString());
  }, [user?.companyId, user?.company?.id]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleSettings = () => {
    handleMenuClose();
    if (user?.companyId) navigate(`/companies/${user.companyId}`);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    clearTimeout(searchTimeoutRef.current);
    if (value.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const response = await axiosInstance.get('/search', { params: { q: value } });
        setSearchResults(response.data?.data || []);
        setSearchOpen(true);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  };

  const handleSearchSelect = (item) => {
    setSearchQuery('');
    setSearchOpen(false);
    setSearchResults([]);
    if (item.type === 'correspondence') navigate(`/correspondences/${item.id}`);
    else if (item.type === 'proceeding') navigate(`/proceedings/${item.id}`);
    else if (item.type === 'document') navigate(`/documents/${item.id}`);
    else if (item.type === 'box') navigate(`/warehouses/boxes/${item.id}`);
    else if (item.type === 'thread') navigate(`/correspondences/${item.correspondenceId}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const hasPermission = (permission) => {
    if (!permission) return true;
    if (!user?.allPermissions) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    return user.allPermissions.includes(permission);
  };

  const menuGroups = [
    {
      label: 'Principal',
      items: [
        { text: 'Dashboard', icon: <DashboardOutlined sx={{ fontSize: 18 }} />, path: '/dashboard', permission: null },
      ],
    },
    {
      label: 'Gestion',
      items: [
        { text: 'Empresas', icon: <BusinessOutlined sx={{ fontSize: 18 }} />, path: isOwner ? '/companies' : `/companies/${user?.companyId}`, permission: 'company.view' },
        { text: 'Correspondencia', icon: <SendOutlined sx={{ fontSize: 18 }} />, path: '/correspondences', permission: 'correspondence.view', module: 'correspondences' },
        { text: 'Plantillas', icon: <ArticleOutlined sx={{ fontSize: 18 }} />, path: '/templates', permission: 'template.view', module: 'templates' },
        { text: 'Expedientes', icon: <FolderOutlined sx={{ fontSize: 18 }} />, path: '/proceedings', permission: 'proceeding.view', module: 'proceedings' },
        { text: 'Dashboard Doc.', icon: <InsertDriveFileOutlined sx={{ fontSize: 18 }} />, path: '/documents/dashboard', permission: 'document.dashboard', module: 'documents' },
        { text: 'Retencion', icon: <DescriptionOutlined sx={{ fontSize: 18 }} />, path: '/retentions', permission: 'retention.view', module: 'retentions' },
        { text: 'Externos', icon: <GroupOutlined sx={{ fontSize: 18 }} />, path: '/entities', permission: 'entity.view', module: 'entities' },
        { text: 'Almacenes', icon: <WarehouseOutlined sx={{ fontSize: 18 }} />, path: '/warehouses', permission: 'warehouse.view', module: 'warehouses' },
      ],
    },
    {
      label: 'Diagnostico',
      items: [
        { text: 'Instrumentos', icon: <AssignmentOutlined sx={{ fontSize: 18 }} />, path: '/surveys', permission: null, ownerOnly: true },
        { text: 'Resultados DX', icon: <InsertDriveFileOutlined sx={{ fontSize: 18 }} />, path: '/surveys/results', permission: null, ownerOnly: true },
        { text: 'Custodia Digital', icon: <StorageOutlined sx={{ fontSize: 18 }} />, path: '/custodia', permission: null, ownerOnly: true },
      ],
    },
    {
      label: 'Sigcod',
      items: [
        { text: 'Solicitudes', icon: <AssignmentOutlined sx={{ fontSize: 18 }} />, path: '/sigcod/solicitudes', permission: null },
        { text: 'Devoluciones', icon: <KeyboardReturnOutlined sx={{ fontSize: 18 }} />, path: '/sigcod/devoluciones', permission: null },
        { text: 'Transferencias', icon: <SwapHorizOutlined sx={{ fontSize: 18 }} />, path: '/sigcod/transferencias', permission: null },
      ],
    },
    {
      label: 'Administracion',
      items: [
        { text: 'Permisos', icon: <VpnKeyOutlined sx={{ fontSize: 18 }} />, path: '/permissions', permission: 'permission.view' },
        { text: 'Roles', icon: <SecurityOutlined sx={{ fontSize: 18 }} />, path: '/roles', permission: 'role.view' },
        { text: 'Usuarios', icon: <PeopleOutlined sx={{ fontSize: 18 }} />, path: '/users', permission: 'user.view' },
        { text: 'Soporte', icon: <ContactSupportOutlined sx={{ fontSize: 18 }} />, path: '/support', permission: null, ownerOnly: true },
        { text: 'Admin Tickets', icon: <AssignmentOutlined sx={{ fontSize: 18 }} />, path: '/support/admin', permission: null, ownerOnly: true },
      ],
    },
  ];

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: SIDEBAR.bg }}>
      {/* Sidebar Header */}
      {isOwner ? (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            borderBottom: `1px solid ${SIDEBAR.divider}`,
          }}
          onClick={() => navigate('/')}
        >
          <Box
            component="img"
            src="/simplia.png"
            alt="Simplia"
            sx={{ width: '100%', height: 'auto', objectFit: 'contain' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            borderBottom: `1px solid ${SIDEBAR.divider}`,
            minHeight: 64,
          }}
          onClick={() => navigate('/')}
        >
          <Avatar
            src={user?.company?.imageUrl || undefined}
            sx={{
              width: 44,
              height: 44,
              bgcolor: '#a0775a',
              fontSize: '0.9375rem',
              fontWeight: 700,
              borderRadius: 0,
              flexShrink: 0,
            }}
          >
            {user?.company?.name?.substring(0, 2).toUpperCase() || 'EM'}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: '"Barlow Condensed", system-ui, sans-serif',
                fontSize: '1.0625rem',
                fontWeight: 600,
                color: '#1d1f20',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.company?.name || 'Empresa'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography sx={{ fontSize: '0.6875rem', color: SIDEBAR.groupLabel }}>
                con tecnologia de
              </Typography>
              <Box
                component="img"
                src="/Favicon.png"
                alt="Simplia"
                sx={{ height: 14, objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Nav Groups */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2, px: 1.5 }}>
        {menuGroups.map((group) => {
          const visibleItems = group.items.filter((item) =>
            hasPermission(item.permission) &&
            (!item.ownerOnly || isOwner) &&
            isModuleEnabled(item.module)
          );
          if (visibleItems.length === 0) return null;
          return (
            <Box key={group.label} sx={{ mb: 1 }}>
              <Typography
                sx={{
                  color: SIDEBAR.groupLabel,
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  px: 1.5,
                  mb: 0.5,
                  mt: 1,
                }}
              >
                {group.label}
              </Typography>
              {visibleItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      sx={{
                        borderRadius: 0,
                        py: 0.75,
                        px: 1.5,
                        minHeight: 38,
                        bgcolor: active ? SIDEBAR.active : 'transparent',
                        borderLeft: active ? `2px solid ${SIDEBAR.activeBorder}` : '2px solid transparent',
                        '&:hover': {
                          bgcolor: active ? SIDEBAR.active : SIDEBAR.hover,
                          '& .nav-text': { color: active ? SIDEBAR.textActive : SIDEBAR.textHover },
                          '& .nav-icon': { color: active ? SIDEBAR.iconActive : '#5980a6' },
                        },
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <ListItemIcon
                        className="nav-icon"
                        sx={{
                          minWidth: 32,
                          color: active ? SIDEBAR.iconActive : SIDEBAR.icon,
                          transition: 'color 0.15s',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        className="nav-text"
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.84375rem',
                          fontWeight: active ? 600 : 500,
                          color: active ? SIDEBAR.textActive : SIDEBAR.text,
                          sx: { transition: 'color 0.15s' },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </Box>
          );
        })}
      </Box>

      {/* User card at bottom */}
      <Box
        sx={{
          px: 1.5,
          py: 1.5,
          borderTop: `1px solid ${SIDEBAR.divider}`,
          bgcolor: SIDEBAR.bg,
        }}
      >
        {(() => {
          const avatarIsUrl = isAvatarUrl(user?.avatar);
          const avatarConfig = avatarIsUrl ? null : getAvatarConfig(user?.avatar);
          const AvatarIcon = avatarConfig?.icon;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 0,
                cursor: 'pointer',
                '&:hover': { bgcolor: SIDEBAR.hover },
                transition: 'background-color 0.15s',
              }}
              onClick={handleMenuOpen}
            >
              <Avatar
                src={avatarIsUrl ? user.avatar : undefined}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: avatarIsUrl ? 'grey.500' : avatarConfig?.color,
                  flexShrink: 0,
                  borderRadius: 0,
                }}
              >
                {!avatarIsUrl && AvatarIcon && <AvatarIcon sx={{ fontSize: 18 }} />}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ color: '#1d1f20', fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.name || 'Usuario'}
                </Typography>
                <Typography sx={{ color: '#98989b', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.company?.name || ''}
                </Typography>
              </Box>
              <KeyboardArrowDown sx={{ color: '#98989b', fontSize: 16, flexShrink: 0 }} />
            </Box>
          );
        })()}
      </Box>
    </Box>
  );

  const avatarIsUrl = isAvatarUrl(user?.avatar);
  const avatarConfig = avatarIsUrl ? null : getAvatarConfig(user?.avatar);
  const AvatarIcon = avatarConfig?.icon;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: '#f2f2f3',
          borderBottom: '1px solid rgba(29,31,32,0.16)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', gap: 1 }}>
          <IconButton
            aria-label="Abrir menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' }, color: '#5d5d60' }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 2, position: 'relative' }}>
            <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
              <Box sx={{ position: 'relative', width: '100%', maxWidth: 480 }}>
                <TextField
                  ref={searchRef}
                  size="small"
                  placeholder="Buscar correspondencias, expedientes, cajas, hilos..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: '#98989b' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#e9e9ea',
                      borderRadius: 0,
                      fontSize: '0.875rem',
                      '& fieldset': { borderColor: 'rgba(29,31,32,0.16)' },
                      '&:hover fieldset': { borderColor: 'rgba(29,31,32,0.16)' },
                      '&.Mui-focused fieldset': { borderColor: '#5980a6' },
                    },
                  }}
                />
                {searchOpen && searchResults.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 0.5,
                      zIndex: 9999,
                      maxHeight: 400,
                      overflowY: 'auto',
                      borderRadius: 0,
                      border: '1px solid rgba(29,31,32,0.16)',
                      boxShadow: '0 12px 32px rgba(43,43,45,.22)',
                    }}
                  >
                    {searchResults.map((item, idx) => (
                      <Box
                        key={`${item.type}-${item.id}-${idx}`}
                        onClick={() => handleSearchSelect(item)}
                        sx={{
                          px: 2,
                          py: 1.25,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': { bgcolor: 'rgba(29,31,32,0.07)' },
                          borderBottom: idx < searchResults.length - 1 ? '1px solid rgba(29,31,32,0.08)' : 'none',
                        }}
                      >
                        <Chip
                          label={
                            item.type === 'correspondence' ? 'Corresp.' :
                            item.type === 'proceeding' ? 'Expediente' :
                            item.type === 'document' ? 'Documento' :
                            item.type === 'box' ? 'Caja' : 'Hilo'
                          }
                          size="small"
                          sx={{
                            fontSize: '0.625rem',
                            height: 20,
                            bgcolor: '#eef6ff',
                            color: '#2c455d',
                            border: 'none',
                            flexShrink: 0,
                            borderRadius: 0,
                          }}
                        />
                        <Typography sx={{ fontSize: '0.8375rem', color: '#1d1f20', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>
          </Box>

          {/* Right side: Company selector + Notifications + User */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isOwner && topbarCompanies.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={activeCompanyId}
                  onChange={async (e) => {
                    const newId = e.target.value;
                    setActiveCompanyId(newId);
                    try {
                      const userId = user?.userId || user?.id;
                      await userService.update(userId, { companyId: newId });
                      window.location.reload();
                    } catch (err) {
                      console.error('Error switching company:', err);
                    }
                  }}
                  displayEmpty
                  renderValue={(val) => {
                    const c = topbarCompanies.find(c => c.id.toString() === val);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#5980a6', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1d1f20' }}>
                          {c?.name || 'Empresa'}
                        </Typography>
                      </Box>
                    );
                  }}
                  sx={{
                    bgcolor: 'transparent',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(29,31,32,0.16)' },
                    '& .MuiSelect-select': { py: 0.75, pr: 3 },
                    fontSize: '0.8125rem',
                  }}
                >
                  {topbarCompanies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => (
                    <MenuItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <NotificationPanel />

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1,
                  minWidth: 220,
                  borderRadius: 0,
                  border: '1px solid rgba(29,31,32,0.16)',
                  boxShadow: '0 12px 32px rgba(43,43,45,.22)',
                },
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid rgba(29,31,32,0.16)',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(29,31,32,0.07)' },
                  transition: 'background-color 0.15s',
                }}
                onClick={handleProfile}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar src={avatarIsUrl ? user?.avatar : undefined} sx={{ width: 36, height: 36, bgcolor: avatarIsUrl ? 'grey.300' : avatarConfig?.color, borderRadius: 0 }}>
                    {!avatarIsUrl && AvatarIcon && <AvatarIcon sx={{ fontSize: 20 }} />}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1d1f20' }}>
                      {user?.name || 'Usuario'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#98989b' }}>
                      {user?.email || ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1.25,
                  px: 2,
                  color: '#EF4444',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  gap: 1.5,
                  '&:hover': { bgcolor: 'rgba(239,68,68,0.06)' },
                }}
              >
                <Logout fontSize="small" />
                Cerrar Sesion
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth, bgcolor: SIDEBAR.bg, borderRight: `1px solid ${SIDEBAR.divider}` },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth, bgcolor: SIDEBAR.bg, borderRight: `1px solid ${SIDEBAR.divider}` },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }} />
        <ImpersonationBanner />
        <Box
          sx={{
            flexGrow: 1,
            py: '22px',
            px: { xs: '14px', md: '26px' },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
